import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import useOnClickOutside from 'root/lib/hooks/useOnClickOutside';
import { playTap } from 'root/lib/audioFunctions';

import RefreshIcon from 'a/hub/components/svgComponents/RefreshIcon';
import BackIcon from 'a/hub/components/svgComponents/BackIcon';
import LogOutIcon from 'a/hub/components/svgComponents/LogOutIcon';
import CloseIcon from 'a/hub/components/svgComponents/CloseIcon';
import { localizedStrings } from 'root/lib/l10n/l10n';

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: 25px;
  left: 50%;
  transform: translateX(-50%);
  cursor: pointer;
`;

const Modal = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: default;
`;

const Content = styled.div`
  width: 800px;
  height: 530px;
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  border-radius: 30px;
  background-color: #f6f6f6;
  box-sizing: border-box;
  z-index: 101;
`;

const Header = styled.h1`
  font-size: 56px;
  font-family: Quicksand, sans-serif;
  color: #393434;
  text-align: center;
  margin: 50px 0 20px;
`;

const Prompt = styled.p`
  font-size: 30px;
  font-family: Quicksand, sans-serif;
  color: #393434;
  text-align: center;
  margin: 20px 100px;
  white-space: pre-wrap;
`;

const ModalButton = styled.button`
  width: 360px;
  height: 96px;
  border-radius: 46.5px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 6px 0 0 rgba(38 78 149 / 25%);
  background-color: #006fe4;
  border-width: 0;
  color: #fff;
  font-size: 30px;
  font-family: 'Quicksand Bold', sans-serif;
  cursor: pointer;
  svg {
    margin: 0 10px 0 20px;
  }
  span {
    margin: 0 20px 0 10px;
  }
`;

const ContinueButton = styled(ModalButton)`
  margin: 20px 30px;
  &:hover:not(:disabled) {
    background-color: #014c9c;
  }
  &:active:not(:disabled) {
    background-color: #00346c;
    box-shadow: none;
  }
  &:focus,
  &:focus-visible {
    background-color: #014c9c;
    box-shadow: 0 0 0 2px #fff, 0 0 0 7px #9c0d63;
    outline: none;
  }
  &:focus:not(:focus-visible) {
    background-color: #00346c;
    box-shadow: none;
  }
  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

const LogoutButton = styled(ModalButton)`
  background-color: #fff;
  color: #006fe4;
  border: solid 5px #006fe4;
  margin: 20px 30px 50px;
  &:hover {
    background-color: #e1e8f9;
  }
  &:active {
    background-color: #c8d6f7;
    box-shadow: none;
  }
  &:focus,
  &:focus-visible {
    background-color: #e1e8f9;
    box-shadow: 0 0 0 2px #fff, 0 0 0 7px #9c0d63;
    outline: none;
  }
  &:focus:not(:focus-visible) {
    background-color: transparent;
    box-shadow: none;
  }
`;

const CloseButton = styled.button`
  width: 85px;
  height: 85px;
  position: absolute;
  top: 15px;
  right: 15px;
  border-width: 0;
  background-color: transparent;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  &:hover {
    background-color: #e1e8f9;
  }
  &:active {
    background-color: #c8d6f7;
  }
  &:focus,
  &:focus-visible {
    box-shadow: 0 0 0 3px #9c0d63;
    outline: none;
  }
  &:focus:not(:focus-visible) {
    box-shadow: none;
  }
`;

const SessionModal = ({
  header,
  prompt,
  continueText,
  onCancel,
  onRefresh,
  onLogout,
  isAlert,
  isMultipleSessions,
  isRefreshButtonActive,
  actionScope,
}) => {
  const continueButtonRef = useRef(null);

  const handleCancel = () => {
    if (!isMultipleSessions) {
      playTap(true, { onEnd: onCancel });
    }
  };

  const handleSessionRefresh = () => playTap(true, { onEnd: onRefresh });

  const handleLogout = () => playTap(true, { onEnd: onLogout });

  const handleKeyDown = ({ key }) => {
    if (key === 'Escape') {
      handleCancel();
    }
  };

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  useEffect(() => {
    continueButtonRef.current.focus();
  }, [continueButtonRef.current]);

  const clickRef = useOnClickOutside(handleCancel);

  return (
    <Wrapper data-dd-action-name={actionScope}>
      <Modal
        ref={clickRef}
        role={isAlert ? 'alert' : 'dialog'}
        aria-labelledby="session-modal-header"
      >
        <Content>
          <Header id="session-modal-header">{header}</Header>
          <Prompt>{prompt}</Prompt>

          <ContinueButton
            ref={continueButtonRef}
            data-dd-action-name={`${actionScope}:continue`}
            aria-label={isMultipleSessions
              ? localizedStrings.modals.multipleSessions.continue
              : localizedStrings.modals.session.keepPlaying}
            data-testid="keepPlaying"
            disabled={!isRefreshButtonActive}
            onClick={isMultipleSessions ? handleSessionRefresh : handleCancel}
          >
            {isMultipleSessions ? <RefreshIcon /> : <BackIcon />}
            <span>{continueText}</span>
          </ContinueButton>

          {!isMultipleSessions && (
            <LogoutButton
              data-dd-action-name={`${actionScope}:logout`}
              aria-label={localizedStrings.logOut}
              data-testid="logoutConfirm"
              onClick={handleLogout}
            >
              <LogOutIcon />
              <span>{localizedStrings.logOut}</span>
            </LogoutButton>
          )}

          {!isMultipleSessions && (
            <CloseButton
              data-dd-action-name={`${actionScope}:close`}
              aria-label="Close dialog"
              data-testid="closeDialog"
              onClick={handleCancel}
            >
              <CloseIcon />
            </CloseButton>
          )}
        </Content>
      </Modal>
    </Wrapper>
  );
};

SessionModal.propTypes = {
  isAlert: PropTypes.bool,
  isMultipleSessions: PropTypes.bool,
  isRefreshButtonActive: PropTypes.bool,
  header: PropTypes.string.isRequired,
  prompt: PropTypes.string.isRequired,
  continueText: PropTypes.string.isRequired,
  onCancel: PropTypes.func,
  onRefresh: PropTypes.func,
  onLogout: PropTypes.func,
  actionScope: PropTypes.string,
};

SessionModal.defaultProps = {
  isAlert: false,
  isMultipleSessions: false,
  isRefreshButtonActive: true,
  onCancel: () => {},
  onRefresh: () => {},
  onLogout: () => {},
  actionScope: 'session-modal',
};

export default SessionModal;
